import type { Customer, Item, ItemStatus, Part, Site } from '../../domain/inventory';
import type { InventorySummary } from '../../api/inventory-api';
import { statusFamilies, statusLabels, type StatusFamily } from '../../domain/status';
import { siteLabel } from '../../lib/site-label';

// Shared cache identity for the full-fleet item fetch that AppShell, Dashboard,
// Sites, and Parts all derive their summaries from — using the same key lets
// them share one ~2.5MB request instead of each fetching it separately.
// Deliberately a distinct top-level key from the paginated/filtered inventory
// list ('items') so mutation invalidation can refresh that visible list
// immediately without forcing an eager refetch of the full fleet on every
// single item save (see EditItemDrawer/TransferDrawer/BatchUpdateDrawer/
// NewItemDrawer, which invalidate this key with refetchType: 'none' instead).
export const FULL_FLEET_ITEMS_QUERY_KEY = ['items-full-fleet'] as const;
export const FULL_FLEET_PAGE_SIZE = 5000;

export interface FamilyMeta {
  family: StatusFamily;
  label: string;
  cssVar: string;
}

// Lifecycle order, with data-quality (STATUS_UNSPECIFIED) set apart at the end
// as a defect indicator rather than a lifecycle stage — see SPEC.md §6 and
// design_handoff_leatherback_ops/README.md's "Semantic — lifecycle families" table.
export const familyOrder: FamilyMeta[] = [
  { family: 'procurement', label: 'Procurement', cssVar: '--family-procurement' },
  { family: 'logistics', label: 'Logistics', cssVar: '--family-logistics' },
  { family: 'deployed', label: 'Deployed', cssVar: '--family-deployed' },
  { family: 'service', label: 'Service', cssVar: '--family-service' },
  { family: 'return', label: 'Return', cssVar: '--family-return' },
  { family: 'storage', label: 'Storage', cssVar: '--family-storage' },
  { family: 'end-of-life', label: 'Scrap', cssVar: '--family-end-of-life' },
  { family: 'data-quality', label: 'Data quality', cssVar: '--family-data-quality' },
];

export interface FamilySegment extends FamilyMeta {
  count: number;
}

export function computeFamilyCounts(items: Item[]): FamilySegment[] {
  const counts = new Map<StatusFamily, number>();
  for (const item of items) {
    const family = statusFamilies[item.status ?? 'STATUS_UNSPECIFIED'];
    counts.set(family, (counts.get(family) ?? 0) + 1);
  }
  return familyOrder.map((meta) => ({ ...meta, count: counts.get(meta.family) ?? 0 }));
}

export interface StatusBreakdownRow {
  status: ItemStatus;
  label: string;
  family: StatusFamily;
  cssVar: string;
  count: number;
}

export function computeStatusBreakdown(items: Item[], limit = 6): StatusBreakdownRow[] {
  const counts = new Map<ItemStatus, number>();
  for (const item of items) {
    const status = item.status ?? 'STATUS_UNSPECIFIED';
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([status, count]) => {
      const family = statusFamilies[status];
      return {
        status,
        label: statusLabels[status],
        family,
        cssVar:
          familyOrder.find((meta) => meta.family === family)?.cssVar ?? '--family-data-quality',
        count,
      };
    })
    .sort((left, right) => right.count - left.count)
    .slice(0, limit);
}

export interface ExceptionRow {
  itemId: string;
  reason: string;
  siteName: string;
}

export function computeExceptionQueue(
  items: Item[],
  sitesById: Map<string, Site>,
  limit = 5,
): ExceptionRow[] {
  return items
    .filter((item) => item.needsAttention)
    .slice(0, limit)
    .map((item) => ({
      itemId: item.id,
      reason: item.attentionReason?.trim() || 'No reason recorded',
      siteName: siteLabel(sitesById.get(item.siteId), item.siteId),
    }));
}

export interface AssignedVsLocationRow {
  siteId: string;
  siteName: string;
  customerName: string | undefined;
  assignedCount: number;
  locatedCount: number;
}

export function computeAssignedVsLocation(
  items: Item[],
  sitesById: Map<string, Site>,
  customersById: Map<string, Customer>,
  limit = 6,
): AssignedVsLocationRow[] {
  const assigned = new Map<string, number>();
  const located = new Map<string, number>();
  for (const item of items) {
    assigned.set(item.siteId, (assigned.get(item.siteId) ?? 0) + 1);
    const locationSiteId = item.locationSiteId ?? item.siteId;
    located.set(locationSiteId, (located.get(locationSiteId) ?? 0) + 1);
  }
  const siteIds = new Set([...assigned.keys(), ...located.keys()]);
  const rows: AssignedVsLocationRow[] = [...siteIds].map((siteId) => {
    const site = sitesById.get(siteId);
    return {
      siteId,
      siteName: siteLabel(site, siteId),
      customerName: site
        ? (customersById.get(site.customerId)?.displayName ??
          customersById.get(site.customerId)?.name)
        : undefined,
      assignedCount: assigned.get(siteId) ?? 0,
      locatedCount: located.get(siteId) ?? 0,
    };
  });
  return rows
    .sort((a, b) => b.assignedCount + b.locatedCount - (a.assignedCount + a.locatedCount))
    .slice(0, limit);
}

export interface PartTypeRow {
  type: string;
  label: string;
  count: number;
}

const PART_TYPE_LABELS: Record<string, string> = {
  CAMERA_SYSTEM: 'Camera system',
  DOCK_BOX: 'Dock box',
  MOUNTING_KIT: 'Mounting kit',
  RADIO_NANOSTATION: 'Radio nanostation',
  SURFACE_CAMERA: 'Surface camera',
  SITE_CONTROLLER: 'Site controller',
  UMBILICAL: 'Umbilical',
  UNIT_CONTROLLER: 'Unit controller',
  WATER_QUALITY_SENSOR: 'Water quality sensor',
  WINCH_SYSTEM: 'Winch system',
  POWERBOX: 'Powerbox',
};

export function computePartTypeBreakdown(
  items: Item[],
  partsById: Map<string, Part>,
  limit = 8,
): PartTypeRow[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const type = partsById.get(item.partId)?.type ?? 'PART_TYPE_UNSPECIFIED';
    if (type === 'PART_TYPE_UNSPECIFIED') continue;
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([type, count]) => ({ type, label: PART_TYPE_LABELS[type] ?? type, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

// Pure derivation over an already-fetched item list — deliberately not its own
// network call. Shared by AppShell (nav badges) and DashboardPage (stat cards)
// so the full fleet is fetched once per session, not once per consumer.
export function computeInventorySummary(items: Item[]): InventorySummary {
  return {
    total: items.length,
    needsAttention: items.filter((item) => item.needsAttention).length,
    active: items.filter((item) => item.status === 'ACTIVE').length,
    service: items.filter((item) => item.status && statusFamilies[item.status] === 'service')
      .length,
    inTransit: items.filter((item) => item.status?.includes('SHIPPED')).length,
    warehouse: items.filter((item) =>
      ['DELIVERED_TO_WAREHOUSE', 'IN_WAREHOUSE_STORAGE'].includes(item.status ?? ''),
    ).length,
  };
}
